import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
    faClose,
    faCoins,
    faFire,
    faPerson,
    faUniversity,
    faUserCircle
} from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";

import { useAuth } from "../../../context/AuthProvider";
import { useLanguage } from "../../../context/LanguageProvider";
import { ROUTES } from "../../constants/routes";
import { formatNumber } from "../../utils/format";
import Button from "../elements/Button";
import ButtonLink from "../elements/ButtonLink";

// Persian-only for now: these can move into i18n later
const LABELS = {
    login: "ورود",
    register: "ثبت‌نام",
    logout: "خروج",
    myCourses: "دوره‌های من",
    streak: "روزهای متوالی",
    credit: "اعتبار",
    xp: "امتیاز",
    openMenu: "منوی حساب کاربری",
    closeMenu: "بستن منو"
};

interface AccountProps {
    className?: string;
}

const Account = ({ className }: AccountProps) => {
    const { user, isAuthenticated, loading, logout } = useAuth();
    const { lang } = useLanguage();

    const [menuOpen, setMenuOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // Close the dropdown on an outside click or Escape
    useEffect(() => {
        if (!menuOpen) return;

        const handleMouseDown = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setMenuOpen(false);
            }
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") setMenuOpen(false);
        };

        document.addEventListener("mousedown", handleMouseDown);
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("mousedown", handleMouseDown);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [menuOpen]);

    const handleLogout = async () => {
        await logout();
        setMenuOpen(false);
    };

    // AuthProvider is still checking the HttpOnly session cookie: show neither the login
    // button nor the user UI until we know the authentication state
    if (loading) return null;

    const stats: { key: string; label: string; value?: number; icon: IconDefinition }[] = [
        { key: "streak", label: LABELS.streak, value: user?.streak, icon: faFire },
        { key: "credit", label: LABELS.credit, value: user?.credit, icon: faCoins },
        { key: "xp", label: LABELS.xp, value: user?.xp, icon: faPerson }
    ];

    const menu = [{ text: LABELS.myCourses, link: ROUTES.myCourses(lang), icon: faUniversity }];

    return (
        <div
            ref={containerRef}
            className={classNames(
                "absolute left-5 top-3 lg:top-0 lg:left-0 lg:mr-auto lg:relative text-md",
                className
            )}
        >
            {isAuthenticated ? (
                <div className="flex items-center">
                    {/* Streak, credit and xp (hidden on phones to save room) */}
                    {stats.map(({ key, label, value, icon }) => (
                        <div
                            key={key}
                            title={label}
                            className="hidden md:flex items-center gap-x-3 ml-8"
                        >
                            <span className="sr-only">{label}</span>
                            <span className="block text-lg text-gray-700 font-bold">
                                {value !== undefined ? formatNumber(value, lang) : "-"}
                            </span>
                            <FontAwesomeIcon icon={icon} size="lg" aria-hidden="true" />
                        </div>
                    ))}

                    {/* The same button opens and closes the dropdown */}
                    <Button
                        onClick={() => setMenuOpen((open) => !open)}
                        className="mr-auto pt-2 rounded-md py-3 px-5 gap-2"
                    >
                        <span className="sr-only">
                            {menuOpen ? LABELS.closeMenu : LABELS.openMenu}
                        </span>
                        <FontAwesomeIcon icon={menuOpen ? faClose : faUserCircle} className="h-5" />
                    </Button>
                </div>
            ) : (
                <ButtonLink
                    to={ROUTES.auth(lang)}
                    className="mr-auto rounded-md text-white text-lg gap-x-3"
                >
                    <span className="hidden md:block">
                        {LABELS.login}
                        <span className="mx-1">/</span>
                        {LABELS.register}
                    </span>

                    <span className="sr-only md:hidden">{LABELS.login}</span>
                    <FontAwesomeIcon icon={faUserCircle} size="xl" className="block md:hidden" />
                </ButtonLink>
            )}

            {/* Account dropdown */}
            {menuOpen && isAuthenticated && (
                <div className="w-64 h-fit rounded-md border-4 border-gray-200 bg-gray-100 absolute left-0 top-20 z-10">
                    <div className="px-5 py-4 border-b-2 border-gray-200">
                        <span className="font-bold text-gray-700">{user?.name}</span>
                    </div>

                    <ul className="list-none w-full">
                        {menu.map((item) => (
                            <li
                                key={item.link}
                                className="first:rounded-t-md border-b-2 border-gray-200 m-0 transition hover:bg-gray-200"
                            >
                                {/* The link fills the row, so the whole row is clickable */}
                                <Link
                                    to={item.link}
                                    onClick={() => setMenuOpen(false)}
                                    className="flex justify-center items-center p-5"
                                >
                                    <FontAwesomeIcon icon={item.icon} className="ml-3" size="lg" />
                                    <span>{item.text}</span>
                                </Link>
                            </li>
                        ))}

                        <li className="last:rounded-b-md hover:bg-gray-200">
                            <button
                                type="button"
                                onClick={handleLogout}
                                className="w-full p-5 text-center text-red-500"
                            >
                                {LABELS.logout}
                            </button>
                        </li>
                    </ul>
                </div>
            )}
        </div>
    );
};

export default Account;